import { useCallback } from "react";

import { Input } from "@/components/forms/Input";
import { Label } from "@/components/forms/Label";

type TGenericNumberInputProps = {
  value?: string | number | null;
  onChange: (value: string) => void;
  customLabel?: string;
  customPlaceholder?: string;
  disabled?: boolean;
  isLabelShown?: boolean;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  id?: string;
};

/**
 * Generic Number Input — common numbers-only textbox
 * Reusable filter component similar to GenericFilterDropdown but renders
 * an <input type="number"> instead of a <select>.
 */
function GenericNumberInput({
  value,
  onChange,
  customLabel,
  customPlaceholder,
  disabled = false,
  isLabelShown = true,
  min,
  max,
  step,
  id,
}: TGenericNumberInputProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Sanitize to numbers only (allow empty string, optional leading minus)
      const raw = e.target.value;
      const sanitized = raw.replace(/[^0-9.-]/g, "");

      onChange(sanitized);
    },
    [onChange],
  );

  const displayValue =
    value === null || value === undefined ? "" : String(value);

  return (
    <div className="w-full">
      {isLabelShown && customLabel && (
        <Label weight="medium">{customLabel}</Label>
      )}

      <Input
        type="number"
        value={displayValue}
        onChange={handleChange}
        placeholder={customPlaceholder}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        className="w-full"
        id={id}
      />
    </div>
  );
}

export default GenericNumberInput;
